/**
 * One chat message — §2.8 #139, #140.
 *
 * Extracted from `ChatInterface`, where it sat below 500 lines of transport,
 * history hydration and scroll management. Purely presentational: it renders
 * the text, rewrites citation markers, and exposes the hover actions.
 *
 * ---
 * **The citation rewrite is the interesting part.** The LLM emits `[1]`,
 * `[2]` in its prose. Those become markdown links with a `cite://<chunk_id>`
 * href, which routes them through a custom `a` renderer that swaps in a
 * `CitationChip`. Two consequences worth knowing:
 *
 * - A marker with **no matching source is left as plain text**, not dropped.
 *   That is the mid-stream state — the tokens arrive before `sources_used` is
 *   persisted — and erasing the number then re-adding it would make the answer
 *   visibly rewrite itself as it streams.
 * - `urlTransform` has to be the identity. ReactMarkdown 10 strips unknown URL
 *   schemes by default, which erases `cite://` before the renderer ever sees
 *   it, and every citation silently becomes nothing.
 *
 * **Pictures** (`![title](img://<id>)`) go through the lesson text's
 * `lessonImageRenderer`: an offered library image or source figure, or a
 * concept visual (`img://cv-<id>`), from the `data-image` parts. Any other
 * image source - an `http(s)` URL the model made up - renders nothing.
 *
 * **Maths** is normalised (`normalizeMath`) before rendering, on every
 * streamed token: remark-math ignores the `\(..\)` / `\[..\]` delimiters
 * models default to.
 *
 * **The hover actions are `opacity-0` until hover**, which hides them from
 * sight but not from the tab order — a keyboard user has no hover, so that is
 * the accessible behaviour rather than an oversight.
 */
import { useMemo, useRef, type ReactNode, type RefObject } from 'react';
import ReactMarkdown from 'react-markdown';
import { Copy as CopyIcon, RotateCcw, StickyNote } from 'lucide-react';
import {
  REMARK_PLUGINS,
  REHYPE_PLUGINS,
  createMarkdownComponents,
} from '@/components/core/markdown/markdown';
import type { ResolvedLibraryImage } from '@/components/core/markdown/markdown';
import { CitationChip } from '@/components/features/learning/media/CitationChip';
import { lessonImageRenderer } from '@/components/features/learning/media/lessonImageRenderer';
import type { CitedByte, ChatSourceCitation } from '@/lib/api-client';
import { normalizeMath } from '@/lib/markdown/normalizeMath';
import type { Visual } from '@/types/visual';

type SourceCitation = ChatSourceCitation;

export interface MessageBubbleProps {
  role: 'user' | 'assistant';
  text: string;
  sources?: SourceCitation[];
  /** `img://<id>` → a library image / source figure the answer shows. */
  images?: Record<string, ResolvedLibraryImage>;
  /** `img://cv-<id>` → a concept visual the answer shows. */
  visuals?: Record<string, Visual>;
  /** The preceding user message, used to title a saved note. */
  previousUserText?: string;
  onSaveToNote?: (question: string, answer: string) => void;
  /** Only supplied for the last assistant bubble, and not while streaming. */
  onRegenerate?: () => void;
}

export function ChatAvatar({ variant }: { variant: 'user' | 'assistant' }) {
  return (
    <div
      aria-hidden='true'
      className={`mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-medium ${
        variant === 'user'
          ? 'bg-primary text-primary-foreground'
          : 'dark:tint-brand bg-brand-100 text-brand-700'
      }`}
    >
      {variant === 'user' ? 'U' : 'AI'}
    </div>
  );
}

// Citation marker rewrite - the LLM emits `[1]`, `[2]`, ... in the prose.
// We rewrite each marker into a markdown link with a `cite://<chunk_id>`
// href so ReactMarkdown routes it through our custom `a` renderer below,
// which swaps in the styled CitationChip. While the answer streams and no
// citation has arrived yet, markers stay as plain text; once they have, a
// marker no passage answers (a number past the passages the tutor was given)
// is dropped rather than left as a stray "[6]".
const CITATION_MARKER_RE = /\[(\d+)\]/g;

/** The citation for marker `[n]`: by its `marker`, or - on a message saved
 *  before citations carried one - by position. */
function citationFor(
  n: number,
  sources: SourceCitation[]
): SourceCitation | undefined {
  if (sources.some(s => s.marker != null)) {
    return sources.find(s => s.marker === n);
  }
  return sources[n - 1];
}

export function rewriteCitations(
  markdown: string,
  sources: SourceCitation[]
): string {
  if (!sources.length) return markdown;
  return markdown.replace(CITATION_MARKER_RE, (_match, num: string) => {
    const src = citationFor(parseInt(num, 10), sources);
    return src ? `[${num}](cite://${src.chunk_id})` : '';
  });
}

// Map our backend `SourceCitation` onto the `CitedByte` shape the
// existing `CitationChip` was built around for lesson text. Both carry
// the same five fields the chip cares about - just under different
// names - so this is a tiny adapter rather than a new component.
function citationToCitedByte(src: SourceCitation): CitedByte {
  return {
    byte_id: src.chunk_id,
    source_id: src.source_id,
    source_name: src.source_name,
    source_type: src.source_type ?? 'text',
    snippet: src.text_preview,
    url: src.url ?? null,
    file_key: src.file_key ?? null,
  };
}

type CitationMap = Map<string, { byte: CitedByte; index: number }>;

function buildCitationMap(sources: SourceCitation[]): CitationMap {
  const map: CitationMap = new Map();
  sources.forEach((s, i) => {
    map.set(s.chunk_id, {
      byte: citationToCitedByte(s),
      index: s.marker ?? i + 1,
    });
  });
  return map;
}

/** The `a` renderer: `cite://<chunk_id>` becomes a CitationChip, looked up
 *  in the CURRENT citations at render time; anything else is a plain link. */
function citationLink(citationsRef: RefObject<CitationMap>) {
  return function CitationLink({
    href,
    children,
    ...rest
  }: {
    href?: string;
    children?: ReactNode;
  }) {
    if (href?.startsWith('cite://')) {
      const citation = citationsRef.current?.get(href.slice('cite://'.length));
      if (!citation) return null;
      return <CitationChip index={citation.index} citation={citation.byte} />;
    }
    return (
      <a href={href} {...rest}>
        {children}
      </a>
    );
  };
}

const NO_IMAGES: Record<string, ResolvedLibraryImage> = {};
const NO_VISUALS: Record<string, Visual> = {};

export function MessageBubble({
  role,
  text,
  sources = [],
  images = NO_IMAGES,
  visuals = NO_VISUALS,
  previousUserText,
  onSaveToNote,
  onRegenerate,
}: MessageBubbleProps) {
  // chunk_id → (citedByte, index), refreshed every render and read by the
  // link renderer through a ref — so the renderer map below can be built
  // once. Rebuilt per render, every override was a new component TYPE and
  // each streamed token re-mounted every fenced plot/chart/diagram in the
  // answer (and every citation chip, closing an open hover card).
  const citationsRef = useRef<CitationMap>(new Map());
  citationsRef.current = buildCitationMap(sources);
  // Same for pictures: they arrive after the text, as `data-image` parts.
  const imagesRef = useRef(images);
  imagesRef.current = images;
  const visualsRef = useRef(visuals);
  visualsRef.current = visuals;

  const renderedText =
    role === 'assistant'
      ? rewriteCitations(normalizeMath(text), sources)
      : text;

  const markdownComponents = useMemo(
    () =>
      createMarkdownComponents({
        a: citationLink(citationsRef),
        img: lessonImageRenderer(imagesRef, visualsRef),
      }),
    []
  );

  return (
    <div
      role='listitem'
      aria-roledescription={
        role === 'assistant' ? 'assistant message' : 'user message'
      }
      className={`flex gap-2 ${role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
    >
      <ChatAvatar variant={role} />

      <div
        className={`group max-w-[80%] ${role === 'user' ? 'items-end' : 'items-start'} flex flex-col`}
      >
        <div
          className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
            role === 'user'
              ? 'rounded-br-md bg-primary text-primary-foreground'
              : 'rounded-bl-md border border-border bg-muted text-foreground'
          }`}
        >
          {role === 'assistant' ? (
            // `[&_pre_code]:text-xs`: prose-sm sizes a code block at 0.857em
            // and the code inside it at 0.857em again - 10.3px, under the
            // 12px floor and hard to read on a phone.
            <div className='prose prose-sm max-w-none dark:prose-invert [&_pre_code]:text-xs'>
              <ReactMarkdown
                remarkPlugins={REMARK_PLUGINS}
                rehypePlugins={REHYPE_PLUGINS}
                components={markdownComponents}
                // ReactMarkdown 10 strips unrecognized URL schemes by
                // default, which would erase our `cite://` href before
                // the custom `a` renderer sees it. Identity transform
                // keeps the scheme intact.
                urlTransform={url => url}
              >
                {renderedText}
              </ReactMarkdown>
            </div>
          ) : (
            <span className='whitespace-pre-wrap break-words'>{text}</span>
          )}
        </div>

        <div
          // Hover-revealed with a mouse; always shown on a phone or any touch
          // screen, which has no hover to reveal them with. `tap-target`
          // gives each 24px action a 44px tap area.
          className={`[&>button]:tap-target mt-1 flex items-center gap-1 opacity-0 transition-opacity duration-200 group-hover:opacity-100 max-sm:opacity-100 [@media(pointer:coarse)]:opacity-100 ${
            role === 'user' ? 'flex-row-reverse' : 'flex-row'
          }`}
        >
          <button
            type='button'
            onClick={() => navigator.clipboard.writeText(text)}
            className='inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent/50 hover:text-muted-foreground'
          >
            <CopyIcon className='h-3 w-3' />
            Copy
          </button>
          {/* Save-to-notes is only meaningful on assistant answers, and
              we need the question (previous user message) to title the
              note. Skip the button if either condition isn't met. */}
          {role === 'assistant' && previousUserText && onSaveToNote && (
            <button
              type='button'
              onClick={() => onSaveToNote(previousUserText, text)}
              className='inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent/50 hover:text-muted-foreground'
            >
              <StickyNote className='h-3 w-3' />
              Save to notes
            </button>
          )}
          {role === 'assistant' && onRegenerate && (
            <button
              type='button'
              onClick={onRegenerate}
              className='inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent/50 hover:text-muted-foreground'
            >
              <RotateCcw className='h-3 w-3' />
              Regenerate
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
